import forge from 'apis/forge';
import { IconType } from 'components/Icon';
import SubmitButton, { SubmitButtonState } from 'components/Widget/Button/SubmitButton';
import ActionButton, { ActionButtonState } from 'components/Widget/Button/ActionButton';
import ChatAttachmentTile, { ChatAttachmentTileState } from 'components/Widget/ChatAttachmentTile';
import MultilineInput from 'components/Widget/Input/MultilineInput';
import { Loader } from 'components/Widget/Loader';
import Thumbnail, { ThumbnailSize } from 'components/Widget/Thumbnail';
import * as api from 'dragonmouse-shared/api/forge';
import {
	Attachment,
	Chat,
	CHAT_COLUMN_GAP,
	ChatMode,
	ChatStatus,
	UserChatEntry,
} from 'dragonmouse-shared/interfaces';
import { MenuItemAction } from 'menu/MenuItem';
import { useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveScene, selectActiveStyle, selectFrames } from 'redux/db/reducers';
import { chatHasFrames, getRightmostFrameX } from 'redux/db/util';
import {
	removeFromSelected,
	setCenterFramesAsChatGeneratesThem,
	setPendingChatInput,
	setSelected,
	showContextMenu,
} from 'redux/mode/actions';
import { Pick } from 'redux/mode/classes';
import { LeftPanelTab, selectIsMobile } from 'redux/mode/reducers';
import { displayViewOnlyModal } from 'redux/mode/util';
import { RootReducer, selectSelectedFrames } from 'redux/reducers';
import store from 'redux/store';
import { FileUploadCallbacks, onUploadFileDialog, onFilePasted, UploadTracker } from 'utils';
import { useTheme } from 'styled-components';
import useToast from 'hooks/useToast';
import { interceptSubmissionIfWalletEmpty } from 'economy';
import QualityToggle from './QualityToggle';
import * as S from './styles';

interface ChatInputProps {
	chat: Chat;
}

const ChatInput = ({ chat }: ChatInputProps) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const { message: toastMessage, isVisible: isToastVisible, showToast } = useToast();
	const {
		scene,
		frames,
		activeSceneId,
		pendingChatInput,
		chatPanelWidth,
		activeStyle,
		selectedFrames,
		isMobile,
	} = useSelector((r: RootReducer) => {
		return {
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			// include deleted frames because the chat can show thumbnails of frames that have been deleted from the canvas
			frames: selectFrames(r.db, true),
			activeSceneId: r.mode.activeSceneId,
			pendingChatInput: r.mode.pendingChatInputs[chat.id],
			chatPanelWidth: r.mode.leftPanelWidths[LeftPanelTab.CHAT],
			activeStyle: selectActiveStyle(r.db, r.mode.activeSceneId),
			selectedFrames: selectSelectedFrames(r),
			isMobile: selectIsMobile(r.mode),
		};
	}, shallowEqual);
	const [prompt, setPrompt] = useState<string>(pendingChatInput?.message || '');
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const inputRef = useRef<HTMLTextAreaElement>(null);

	useEffect(() => {
		if (!pendingChatInput) {
			dispatch(
				setPendingChatInput({
					chatId: chat.id,
					pendingChatInput: {
						frameIds: [],
						message: '',
						chatMode: ChatMode.DESIGN_AND_CHAT,
					},
				})
			);
		}
	}, [chat.id, pendingChatInput, dispatch]);

	const stoppable = chat?.metadata?.status === ChatStatus.STOPPABLE;
	const showSpinner = chat?.metadata?.status === ChatStatus.PROCESSING;

	// Validation logic for whether input can be submitted
	const hasLoadingAttachments = useMemo(
		() => pendingChatInput?.loadingAttachments && pendingChatInput.loadingAttachments.length > 0,
		[pendingChatInput?.loadingAttachments]
	);
	const minimalInputEntered = useMemo(
		() =>
			prompt.length > 0 ||
			(pendingChatInput?.attachments && pendingChatInput.attachments.length > 0),
		[prompt, pendingChatInput?.attachments]
	);
	const canSubmit = minimalInputEntered && !hasLoadingAttachments;

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onSubmit = async () => {
		if (!activeSceneId || !chat) return;

		if (!!scene?.viewOnly) {
			displayViewOnlyModal();
			return;
		}

		// Refresh wallet to ensure we have the latest balance before checking
		try {
			await forge.fetchWallet();
		} catch (error) {
			console.error('Failed to refresh wallet before submission:', error);
			// Continue anyway - we'll use cached wallet data
		}

		if (interceptSubmissionIfWalletEmpty()) {
			return;
		}

		dispatch(setCenterFramesAsChatGeneratesThem(true));

		const updateRequest: api.UpdateChatRequest = {
			update: {
				id: chat.id,
				metadata: {
					status: ChatStatus.PROCESSING,
					statusMessage: '',
				},
			},
		};

		// If the chat has yet to place any frames on the canvas, recompute its X position
		//
		// This handles the situation where the user creates a chat, generates some frames that
		// overlap with the chat's dedicated column using generate or something else, and then chats
		// to generate frames.
		// We were seeing behavior where this awkwardly initiated the chat's column to have those
		// generated frames in it! gross
		// This was especially column when starting a scene. We automatically created a chat. So if the
		// user did a bunch of stuff, then chatted, the chat wouldn't start to the left of the other stuff.
		// This fix also improves the UX when a chat is used but not to generate images.
		if (!chatHasFrames(chat)) {
			const rightmostFrameX = getRightmostFrameX();
			if (rightmostFrameX !== null) {
				updateRequest.update.x = rightmostFrameX + CHAT_COLUMN_GAP;
			}
		}

		await forge.updateChat(updateRequest);

		const entry: UserChatEntry = {
			kind: 'user',
			message: prompt,
			sourceFrameIds: thumbnailFrameIds,
			attachments: pendingChatInput.attachments || [],
		};

		const req: api.AddUserEntryChatRequest = {
			chatId: chat.id,
			entry,
			chatMode: pendingChatInput.chatMode,
			style: activeStyle,
			quality: store.getState().mode.chatQuality,
		};
		await forge.addUserEntry.withToast()(req);

		// clear selected frames
		// the selected frames were part of thumbnailFrameIds, which have now been sent to the LLM
		// so let's deselect them because the user doesn't expect to include them in the next chat input
		dispatch(setSelected([]));
		setPrompt('');

		dispatch(
			setPendingChatInput({
				chatId: chat.id,
				pendingChatInput: {
					...pendingChatInput,
					frameIds: [],
					message: '',
					attachments: [],
				},
			})
		);
	};

	const onStop = async () => {
		if (!activeSceneId || !chat) return;

		await forge.updateChat({
			update: {
				id: chat.id,
				metadata: {
					status: ChatStatus.PROCESSING,
					statusMessage: 'Stopping...',
				},
			},
		});
		const req: api.StopChatGenerationRequest = {
			chatId: chat.id,
		};
		await forge.stopChatGeneration.withToast()(req);
	};

	const onChangePrompt = (e: any) => {
		setPrompt(e.target.value);
		dispatch(
			setPendingChatInput({
				chatId: chat.id,
				pendingChatInput: {
					...pendingChatInput,
					message: e.target.value,
				},
			})
		);
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
		// On desktop: Enter submits, Shift+Enter adds line break
		// On mobile: Enter adds line break, user taps button to submit
		if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
			e.preventDefault();
			if (canSubmit) {
				onSubmit();
			}
		}
	};

	const onRemoveThumbnail = (frameId: string) => {
		if (pendingChatInput.frameIds.includes(frameId)) {
			dispatch(
				setPendingChatInput({
					chatId: chat.id,
					pendingChatInput: {
						...pendingChatInput,
						frameIds: pendingChatInput.frameIds.filter((id) => id !== frameId),
					},
				})
			);
		} else if (selectedFrames.some((f) => f.id === frameId)) {
			dispatch(removeFromSelected(new Pick(frameId)));
		}
	};

	const onRemoveAttachment = (storagePath: string) => {
		dispatch(
			setPendingChatInput({
				chatId: chat.id,
				pendingChatInput: {
					...pendingChatInput,
					attachments: pendingChatInput.attachments?.filter((a) => a.storagePath !== storagePath),
				},
			})
		);
	};

	const handlePaste = (text: string) => {
		// Insert the pasted text at the current cursor position in the prompt
		const newPrompt = prompt + text;
		setPrompt(newPrompt);
		dispatch(
			setPendingChatInput({
				chatId: chat.id,
				pendingChatInput: {
					...pendingChatInput,
					message: newPrompt,
				},
			})
		);

		// Focus the input after pasting
		setTimeout(() => {
			inputRef.current?.focus();
		}, 0);
	};

	const onContextMenu = async (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (e.target instanceof HTMLElement) {
			const paste = await MenuItemAction.createPasteText(handlePaste);
			const menuItems = [paste];
			const contextMenuAction = showContextMenu({
				point: { x: e.clientX + 5, y: e.clientY },
				menuItems,
			});
			dispatch(contextMenuAction);
		}
	};

	// File upload callbacks using Redux dispatch
	const fileCallbacks: FileUploadCallbacks = useMemo(
		() => ({
			onLoadingStart: (uploadTracker: UploadTracker) => {
				dispatch(
					setPendingChatInput({
						chatId: chat.id,
						pendingChatInput: {
							...pendingChatInput,
							loadingAttachments: [...(pendingChatInput?.loadingAttachments || []), uploadTracker],
						},
					})
				);
			},
			onLoadingEnd: (uniqueId: string) => {
				dispatch(
					setPendingChatInput({
						chatId: chat.id,
						pendingChatInput: {
							...pendingChatInput,
							loadingAttachments: pendingChatInput?.loadingAttachments?.filter(
								(a) => a.uniqueId !== uniqueId
							),
						},
					})
				);
			},
			onSuccess: (attachment: Attachment) => {
				// Check if file already exists
				if (pendingChatInput?.attachments?.some((a) => a.storagePath === attachment.storagePath)) {
					// This will be handled by the utility's toast in the future if needed
					return;
				}
				dispatch(
					setPendingChatInput({
						chatId: chat.id,
						pendingChatInput: {
							...pendingChatInput,
							attachments: [...(pendingChatInput?.attachments || []), attachment],
						},
					})
				);
			},
			onError: (error: Error) => {
				console.error('Error uploading file:', error);
			},
		}),
		[chat.id, dispatch, pendingChatInput]
	);

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const thumbnailFrameIds = useMemo(() => {
		const pendingIds = pendingChatInput?.frameIds || [];
		const selectedIds = selectedFrames.map((f) => f.id);

		// Find newly selected frames that aren't already in pending chat input and sort them by X position (left to right)
		const sortedNewIds = selectedIds
			.filter((id) => !pendingIds.includes(id))
			.sort((a, b) => {
				const frameA = frames.find((f) => f.id === a);
				const frameB = frames.find((f) => f.id === b);
				if (!frameA || !frameB) return 0;
				// Sort by X first (left to right), then by Y descending (top to bottom)
				if (frameA.x !== frameB.x) return frameA.x - frameB.x;
				return frameA.y - frameB.y;
			});

		// Return existing pending frames followed by newly selected frames in sorted order
		return [...pendingIds, ...sortedNewIds];
	}, [pendingChatInput?.frameIds, selectedFrames, frames]);

	const renderThumbnails = () => {
		if (!thumbnailFrameIds.length) return null;
		return (
			<S.Thumbnails
				onWheel={(e) => {
					e.stopPropagation();
					e.currentTarget.scrollLeft += e.deltaX;
				}}
			>
				{thumbnailFrameIds.map((frameId) => (
					<Thumbnail
						key={frameId}
						frameId={frameId}
						onDelete={() => onRemoveThumbnail(frameId)}
						size={ThumbnailSize.CHAT}
						selectOnClick
					/>
				))}
			</S.Thumbnails>
		);
	};

	const renderAttachments = () => {
		const allAttachments = [
			...(pendingChatInput?.attachments || []),
			...(pendingChatInput?.loadingAttachments || []).map((attachment) => ({
				...attachment,
				storagePath: `loading-${attachment.uniqueId}`, // Use uniqueId for loading attachments
			})),
		];

		if (!allAttachments.length) return null;
		return (
			<S.Attachments
				onWheel={(e) => {
					e.stopPropagation();
					e.currentTarget.scrollLeft += e.deltaX;
				}}
			>
				{allAttachments.map((attachment) => {
					const isLoading = pendingChatInput?.loadingAttachments?.some(
						(a) => a.uniqueId === attachment.storagePath.replace('loading-', '')
					);
					return (
						<ChatAttachmentTile
							key={attachment.storagePath}
							attachment={attachment}
							onDelete={isLoading ? undefined : () => onRemoveAttachment(attachment.storagePath)}
							state={isLoading ? ChatAttachmentTileState.LOADING : ChatAttachmentTileState.REGULAR}
						/>
					);
				})}
			</S.Attachments>
		);
	};

	const renderAttachmentButton = () => {
		return (
			<ActionButton
				icon={IconType.ADD}
				label={undefined}
				tooltip="Upload attachment"
				onClick={() => {
					if (!!scene?.viewOnly) {
						displayViewOnlyModal();
						return;
					}
					onUploadFileDialog(fileCallbacks);
				}}
			/>
		);
	};

	const renderChatModeToggle = () => {
		const isChatOnly = pendingChatInput?.chatMode === ChatMode.CHAT_ONLY;
		return (
			<ActionButton
				state={isChatOnly ? ActionButtonState.SELECTED : ActionButtonState.REGULAR}
				icon={isChatOnly ? IconType.CHAT : IconType.STYLE}
				tooltip={isChatOnly ? 'Turn off Chat-only Mode' : 'Turn on Chat-only Mode'}
				colors={{
					selected: theme.green[200],
					hover: isChatOnly ? theme.green[400] : theme.gray[200],
					active: 'transparent',
					text: isChatOnly ? theme.green[800] : theme.gray[1000],
				}}
				onClick={() => {
					const nextChatMode =
						pendingChatInput?.chatMode === ChatMode.DESIGN_AND_CHAT
							? ChatMode.CHAT_ONLY
							: ChatMode.DESIGN_AND_CHAT;
					dispatch(
						setPendingChatInput({
							chatId: chat.id,
							pendingChatInput: {
								...pendingChatInput,
								chatMode: nextChatMode,
							},
						})
					);

					if (isMobile) {
						showToast(
							nextChatMode === ChatMode.CHAT_ONLY ? 'Chat-only mode' : 'Chat + design mode'
						);
					}
				}}
			/>
		);
	};

	const renderActions = () => {
		return (
			<S.Actions isMobile={isMobile}>
				{!isMobile && chatPanelWidth > 120 && renderAttachmentButton()}
				{renderChatModeToggle()}
				<S.Spacer />
				<QualityToggle />
				<S.LilWidthSpacer />
				{showSpinner ? (
					<S.LoadingWrapper isMobile={isMobile}>
						<Loader width={isMobile ? 32 : 22} height={isMobile ? 32 : 22} />
					</S.LoadingWrapper>
				) : (
					<S.SubmitButtonWrapper isMobile={isMobile}>
						<SubmitButton
							state={
								stoppable || canSubmit ? SubmitButtonState.REGULAR : SubmitButtonState.DISABLED
							}
							colors={
								pendingChatInput?.chatMode === ChatMode.CHAT_ONLY
									? {
											disabled: theme.green[200],
											hover: theme.green[600],
											active: theme.green[800],
									  }
									: undefined
							}
							type={stoppable ? IconType.STOP : IconType.LINE_ARROW_UP}
							tooltip={stoppable ? 'Stop' : 'Submit'}
							tooltipPlacement="bottom-start"
							onClick={() => (stoppable ? onStop() : onSubmit())}
						/>
					</S.SubmitButtonWrapper>
				)}
			</S.Actions>
		);
	};

	return (
		<>
			{isMobile && toastMessage && (
				<S.MobileToast isVisible={isToastVisible} role="status" aria-live="polite">
					{toastMessage}
				</S.MobileToast>
			)}
			<S.Base isMobile={isMobile}>
				<S.Prompt isFocused={isFocused}>
					{renderAttachments()}
					{renderThumbnails()}
					<S.MultilineInputWrapper isMobile={isMobile} onPaste={onFilePasted(fileCallbacks)}>
						<MultilineInput
							// this data attribute is part of a filthy hack to focus the chat input
							data-chat-input="true"
							ref={inputRef}
							value={prompt}
							onInput={onChangePrompt}
							onKeyDown={onKeyDown}
							placeholder={'Design, critique, explore'}
							minLines={isMobile ? 1 : 4}
							renderBorder={false}
							variant={isMobile ? 'large' : 'medium'}
							onFocusChange={(isFocused) => {
								setIsFocused(isFocused);
							}}
							onContextMenu={onContextMenu}
						/>
					</S.MultilineInputWrapper>
					{renderActions()}
				</S.Prompt>
			</S.Base>
		</>
	);
};

export default ChatInput;
